/**
 * useGDriveFolderPicker
 *
 * Headless logic for the folder-selection step from
 * docs/features/gdrive-folder-scoping/SPEC.md §5.1 (connect-time, mandatory)
 * and §5.3 (manage editor "Add folders"): merges newly picked folders with
 * the freshest known folder list (deduped by id) and persists via
 * `PUT /api/auth/gdrive/channels/:channelId/folders`.
 *
 * Deliberately headless (no rendered UI, no wrapping overlay of any kind) —
 * see the 2026-07-31 positioning-bug postmortem in GDriveFolderPickerPanel.tsx.
 * Any full-screen overlay (even a plain, non-Radix `createPortal` backdrop
 * with `backdrop-filter`/scroll-lock) mounted while the Google Picker opens
 * throws off the Picker's own viewport-fixed positioning — see the
 * "Google Picker — viewport-fixed positioning overrides" comment block in
 * `index.css`. `openPicker()` must always be called directly from a plain
 * `<button onClick>` with nothing else in the tree fighting for position or
 * scroll — exactly like `MediaSection.tsx`'s `openGDrivePicker`.
 *
 * Add/remove race safety (code-review finding): pass the SAME
 * `useUpdateGDriveFolders()` instance already used elsewhere (e.g.
 * `GDriveFoldersSection`'s Remove buttons) via the `mutation` option so Add
 * and Remove share a single `isPending` flag — callers should disable their
 * trigger buttons while `isSaving` is true so a stale merge can never race a
 * concurrent PUT. Omit `mutation` for standalone callers with nothing to
 * race against (e.g. a fresh connect-time step).
 *
 * Access-mode extension (docs/features/gdrive-folder-scoping/ACCESS-MODE.md):
 * also exposes `shareEntireDrive()`, a mode-only PUT (`{ mode: 'all' }`,
 * no Picker) sharing the same mutation instance, for the "whole Drive"
 * choice at any of the three call sites (unconfigured prompt, or switching a
 * scoped channel to unrestricted from the manage editor).
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGDrivePicker } from '@/hooks/useGDrivePicker';
import {
  useGDriveFolders,
  useUpdateGDriveFolders,
  type UpdateGDriveFoldersMutation,
} from '@/hooks/useGDriveImport';
import { formatApiError } from '@/services/api';
import { toast } from '@/utils/toast';
import type { GDrivePickerFile, GDriveSharedFolder } from '@/types/gdrive';

interface UseGDriveFolderPickerOptions {
  channelId: number;
  /** Folders already shared — merged (deduped) with newly picked ones. */
  existingFolders?: GDriveSharedFolder[];
  onSaved?: (folders: GDriveSharedFolder[]) => void;
  /** Shared mutation instance — see race-safety note above. */
  mutation?: UpdateGDriveFoldersMutation;
}

interface UseGDriveFolderPickerReturn {
  /** Opens the native Google Picker directly — call from a plain onClick. */
  openPicker: () => void;
  /**
   * Switches the channel to access-mode `'all'` (whole Drive, ACCESS-MODE
   * extension) — no Picker involved, just a PUT. Shares the same mutation
   * instance as `openPicker`'s save (see race-safety note above).
   */
  shareEntireDrive: () => void;
  isSaving: boolean;
  pickerError: Error | null;
}

export function useGDriveFolderPicker({
  channelId,
  existingFolders = [],
  onSaved,
  mutation,
}: UseGDriveFolderPickerOptions): UseGDriveFolderPickerReturn {
  const { t } = useTranslation('settings');
  const ownMutation = useUpdateGDriveFolders();
  const updateFolders = mutation ?? ownMutation;

  // Read the folder list straight from the query cache (shares the same
  // queryKey as any parent's useGDriveFolders call) rather than trusting only
  // the `existingFolders` prop — closes the gap where a concurrent Remove's
  // invalidation could otherwise land after this hook last rendered.
  const { data: freshFoldersData } = useGDriveFolders(channelId);
  const baseFolders = freshFoldersData?.folders ?? existingFolders;

  const handleFoldersPicked = useCallback(
    (picked: GDrivePickerFile[]) => {
      if (picked.length === 0) return;

      const pickedFolders: GDriveSharedFolder[] = picked.map((f) => ({ id: f.id, name: f.name }));
      const merged = new Map<string, GDriveSharedFolder>();
      for (const folder of baseFolders) merged.set(folder.id, folder);
      for (const folder of pickedFolders) merged.set(folder.id, folder);
      const nextFolders = Array.from(merged.values());

      updateFolders.mutate(
        { channelId, folders: nextFolders },
        {
          onSuccess: (data) => {
            toast.success(
              t('gdrive_folders_saved', {
                defaultValue: 'Shared folders updated.',
              }),
            );
            onSaved?.(data.folders);
          },
          onError: (error) => {
            // Surface the server's own message (e.g. the
            // GDRIVE_MAX_SHARED_FOLDERS cap) instead of a generic string.
            const { message } = formatApiError(error);
            toast.error(
              message ||
                t('gdrive_folders_save_error', {
                  defaultValue: 'Failed to save shared folders. Please try again.',
                }),
            );
          },
        },
      );
    },
    [channelId, baseFolders, onSaved, t, updateFolders],
  );

  const { open: openFolderPickerRaw, error: pickerError } = useGDrivePicker({
    channelId,
    mode: 'folders',
    onSelect: handleFoldersPicked,
  });

  const openPicker = useCallback(() => {
    void openFolderPickerRaw();
  }, [openFolderPickerRaw]);

  const shareEntireDrive = useCallback(() => {
    updateFolders.mutate(
      { channelId, mode: 'all' },
      {
        onSuccess: (data) => {
          toast.success(
            t('gdrive_folders_saved', {
              defaultValue: 'Shared folders updated.',
            }),
          );
          onSaved?.(data.folders);
        },
        onError: (error) => {
          const { message } = formatApiError(error);
          toast.error(
            message ||
              t('gdrive_folders_save_error', {
                defaultValue: 'Failed to save shared folders. Please try again.',
              }),
          );
        },
      },
    );
  }, [channelId, onSaved, t, updateFolders]);

  return { openPicker, shareEntireDrive, isSaving: updateFolders.isPending, pickerError };
}
